/**
 * Returns true when the value is neither null nor undefined.
 *
 * @param {unknown} value - the value to check
 * @returns {boolean}
 */
const doesExist = (value: unknown): boolean => {
  return value !== null && value !== undefined
}

/**
 * Reads the API base URL from the Vite environment, falling back to the
 * production Cloud Functions endpoint. Centralised so we can swap targets
 * per deployment surface (web / embed / Capacitor) without code changes.
 *
 * @returns {string}
 */
const getApiBaseUrl = (): string => {
  const fromEnvironment = import.meta.env.VITE_API_URL

  if (typeof fromEnvironment === 'string' && fromEnvironment.length > 0) {
    return fromEnvironment
  }

  return 'https://api.quickcasa.ai'
}

/**
 * Returns the canonical origin of the QuickPass app — used anywhere we hand
 * out absolute URLs that must always point back to the standalone app
 * regardless of where the code is currently running (magic-link emails,
 * cross-site SDK targets, etc.). Reads from `VITE_APP_BASE_URL` so
 * Capacitor and dev builds can override the default.
 *
 * Falls back to `window.location.origin` in a browser context (correct for
 * the standalone web build, since the code already runs on the app origin),
 * and finally to the production app URL when no other source is available.
 *
 * @returns {string}
 */
const getAppBaseUrl = (): string => {
  const fromEnvironment = import.meta.env.VITE_APP_BASE_URL

  if (typeof fromEnvironment === 'string' && fromEnvironment.length > 0) {
    return fromEnvironment.replace(/\/$/u, '')
  }

  if (typeof window !== 'undefined' && typeof window.location !== 'undefined') {
    return window.location.origin
  }

  return 'https://app.quickpass.club'
}

export { doesExist, getApiBaseUrl, getAppBaseUrl }
